import React, { useEffect, useRef, useState } from 'react';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import { Map } from 'leaflet';
import { NewRouteConfirmationMap } from './components/NewRouteConfirmationMap';
import { Mission, LandingPad, Order, PublicOrder, PlaceContext } from '@m4a3/api-client';
import { useAppSelector } from 'hooks/redux';
import { selectOrderDestinationLandingPad, selectOrderOriginLandingPad, selectOrderLandingZoneCoords, selectSelectedCellId } from 'selectors/ordersSelector';
import { RoutePoint, RoutePointType } from './RouteConfirmation';
import { orderfulApi } from 'services/api-instances';
import { fetchAirspace } from 'services/airspaceApi';
import { parseFlightRoutes } from 'views/DevTools/tools';
import { connect, useDispatch } from 'react-redux';
import { newNotification } from 'actions/uiActions';
import { finaliseRouteThunk, getOrder, upsertOrderCardById } from 'actions/orderActions';
import { espooTestSiteCellId, espooCellId } from '../../constants';

interface NewRouteConfirmationProps {
  open: boolean;
  onClose: () => void;
  order: Order;
}

const NewRouteConfirmation = ({ open, onClose, order, notify }: NewRouteConfirmationProps & { notify: any }) => {
  const mapRef = useRef<Map>();
  const dispatch = useDispatch();
  const [fullOrder, setFullOrder] = useState<PublicOrder>(null);
  const landingZoneCoords = useAppSelector((state) => selectOrderLandingZoneCoords(state, order?.landingZoneId));
  const originLandingPad = useAppSelector((state) => selectOrderOriginLandingPad(state, order?.originLandingPadId));
  const destinationLandingPad = useAppSelector((state) => selectOrderDestinationLandingPad(state, order?.destinationLandingPadId));
  const [missionToConfirm, setMissionToConfirm] = useState<Mission>(order?.mission);
  const [unchangedRouteCoords, setUnchangedRouteCoords] = useState<RoutePoint[]>([]);
  const [geoTunnel, setGeoTunnel] = useState<any>();
  const [missionRoutes, setMissionRoutes] = useState<any>({});
  const selectedCellId = useAppSelector(selectSelectedCellId);
  const [addressInfo, setAddressInfo] = useState<PlaceContext>();

  const shouldRouteBeSymmetrical = (o: Order): boolean => {
    // Disable asymmetric route editing on Espoo and Espoo Test Site
    return [espooCellId, espooTestSiteCellId].includes((o as any)?.cellId);
  };

  useEffect(() => {
    getLatestOrderInfo().then(o => {
      console.log('o', o);
      // @ts-ignore
      setFullOrder(o);
      const missionRouteCoords = extractCouteCoordsFromMission(o?.mission);
      setUnchangedRouteCoords(missionRouteCoords);
      setMissionToConfirm(o?.mission);
    });
  }, [order?.id]);

  useEffect(() => {
    if (selectedCellId === undefined || selectedCellId === null) { return; }
    fetchAirspace(selectedCellId)
      .then(res => {
        const missions: any = parseFlightRoutes(res.data);
        setMissionRoutes(missions);
      })
      .catch(() => {});
  }, [selectedCellId]);

  const getLatestOrderInfo = async () => {
    return await dispatch(getOrder(order.id)) as unknown as Order;
  };

  useEffect(() => {
    const gt = (fullOrder as any)?.mission?.geoJsonGeotunnel || (missionToConfirm as any)?.geoJsonGeotunnel;
    if (gt) {
      setGeoTunnel(gt);
    }
  }, [fullOrder, missionToConfirm]);

  useEffect(() => {
    const eircode = order?.eircode;
    if (!eircode) { return; }
    orderfulApi.getPlaceContextByEircode(eircode.trim().toUpperCase())
      .then(res => {
        setAddressInfo(res);
      })
      .catch(() => {});
  }, [order?.eircode]);

  const extractCouteCoordsFromMission = (mission: Mission): RoutePoint[] => {
    // @ts-ignore
    const missionFeatureCoordinates: RoutePoint[] = mission?.geoJson?.features.map(feature => {
      let type = feature.properties.type;
      if (feature.properties.type === 'point') {
        type = RoutePointType.WAYPOINT;
      }
      return {
        coordinates: [feature.geometry.coordinates[1], feature.geometry.coordinates[0]] as [number, number],
        type: type,
        index: Number(feature.properties.index)
      };
    });

    if (!missionFeatureCoordinates || missionFeatureCoordinates.length === 0) {
      return missionFeatureCoordinates;
    }

    // If symmetrical, strictly rebuild inbound as a mirror of outbound around the drop point
    if (shouldRouteBeSymmetrical(order)) {
      const original = missionFeatureCoordinates.map(p => ({ ...p }));
      const dropIdx = original.findIndex(c => c.type === RoutePointType.CUSTOMER_DROP_LOCATION);
      if (dropIdx === -1) {
        return original;
      }

      // Outbound: from start up to and including drop
      const outbound = original.slice(0, dropIdx + 1).map(p => ({ ...p }));
      // Mirror source for inbound excludes the drop
      const inboundMirrorSource = outbound
        .slice(0, -1) // exclude drop
        .filter(p => p.type === RoutePointType.WAYPOINT) // only mirror waypoints
        .reverse();

      // Keep the original final non-WAYPOINT end point unchanged
      const endOriginal = original[original.length - 1];

      // Build inbound as mirrored WAYPOINTS only, then append original end
      const inboundWaypoints = inboundMirrorSource.map((p) => ({
        coordinates: [p.coordinates[0], p.coordinates[1]] as [number, number],
        type: RoutePointType.WAYPOINT,
        index: 0
      } as RoutePoint));

      const rebuilt = [...outbound, ...inboundWaypoints, endOriginal].map((p, idx) => ({ ...p, index: idx }));
      return rebuilt;
    }
    return missionFeatureCoordinates;
  };


  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="new-route-confirmation-title"
      aria-describedby="new-route-confirmation-description">
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '94%',
          height: '88vh',
          maxWidth: 'none',
          borderRadius: 24,
          backgroundColor: 'white'
        }}>
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Top header bar */}
          <div style={{ flex: '0 0 auto' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 16px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h2 id="new-route-confirmation-title" style={{ margin: 0, fontSize: 24, fontWeight: 600, color: '#111827' }}>
                  Edit Route
                </h2>
              </div>
              <Button onClick={onClose} style={{ minWidth: 32, width: 32, height: 32, borderRadius: 16, padding: 0 }} aria-label="Close">
                <CloseIcon />
              </Button>
            </div>
          </div>

          {/* Content below header (map fills remaining height) */}
          <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0 }}>
            <NewRouteConfirmationMap
              intialCoords={landingZoneCoords}
              mapRef={mapRef}
              landingZoneId={order.landingZoneId}
              initialRouteCoords={unchangedRouteCoords}
              isSymmetricalRoute={shouldRouteBeSymmetrical(order)}
              geoJson={missionToConfirm?.geoJson}
              order={order}
              missionRoutes={missionRoutes}
              geoTunnel={geoTunnel}
              placeContext={addressInfo}
              originLandingPad={originLandingPad}
              destinationLandingPad={destinationLandingPad}
              onDeleteMissionAirspaceSuccess={onClose}
              onUpdateRouteSuccess={() => {
                notify({ variant: 'success', message: 'Update route' });
                // Refresh orders and any other follow-up like the old component
                dispatch(finaliseRouteThunk(order.id));
                // Update SSE-backed order list entry
                dispatch(upsertOrderCardById(order.id));
                // Refresh order and route/geo tunnel from mission
                getLatestOrderInfo().then(o => {
                  // @ts-ignore
                  setFullOrder(o);
                  const missionRouteCoords = extractCouteCoordsFromMission((o as any)?.mission as any);
                  setUnchangedRouteCoords(missionRouteCoords);
                  const gt = (o as any)?.mission?.geoJsonGeotunnel;
                  if (gt) { setGeoTunnel(gt); }
                }).catch(() => {});
              }}
              onUpdateRouteError={(err) => {
                notify({ variant: 'error', message: 'Failed to update route: ' + (err?.response?.data?.errorDetail || 'Unknown error') });
              }}
              onReserveRouteSuccess={(updatedOrder: any) => {
                notify({ variant: 'success', message: 'Route reserved' });
                // Use returned order with mission
                if (updatedOrder) {
                  setFullOrder(updatedOrder);
                  const missionRouteCoords = extractCouteCoordsFromMission((updatedOrder as any)?.mission as any);
                  setUnchangedRouteCoords(missionRouteCoords);
                  const gt = (updatedOrder as any)?.mission?.geoJsonGeotunnel;
                  if (gt) { setGeoTunnel(gt); }
                }
                // Refresh orders list
                dispatch(upsertOrderCardById((updatedOrder as any)?.id || order.id));
              }}
              onReserveRouteError={(err) => {
                notify({ variant: 'error', message: 'Failed to reserve route: ' + (err?.response?.data?.errorDetail || 'Unknown error') });
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

const mapDispatchToProps = dispatch => ({
  notify: (notification) => dispatch(newNotification(notification))
});

export default connect(null, mapDispatchToProps)(NewRouteConfirmation);


